import axios from "axios";
import { getUserEmail } from "./utils";

export interface SyncAdobeSignResult {
    results: PromiseSettledResult<any>[];
    hasRateLimitError: boolean;
}

const makeParallelAdobeSignRequests = (RecordIdsList: any[]) => {
    const loginUserEmail = getUserEmail();
    const apiUrl = `${process.env.REACT_APP_PROXY_URL}/v1/syncAdobeStatus`;
    return RecordIdsList.map(recordId => (axios.post(apiUrl, null, {
        headers: {
            Authorization: "Basic " + process.env.REACT_APP_AUTHORIZATION_KEY,
            "x-merck-apikey": process.env.REACT_APP_X_MERCK_API_KEY,
        },
        params: {
            record_id: recordId,
            email: loginUserEmail ?? "",
        },
    })));
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const getWaitTime = (retryCount: number, failedRequests: any[]) => {
    const BASE_BACKOFF_SECONDS = 2;
    const MAX_BACKOFF_SECONDS = 60;
    let waitTime = 0;

    for (const request of failedRequests) {
        const errorResponse = request.reason?.response;
        waitTime = errorResponse.headers['retry-after'] || errorResponse.headers['retryRetry-After'] ||
            errorResponse.headers['RETRY-AFTER'];

        return waitTime * 1000;
    };

    waitTime = Math.min(BASE_BACKOFF_SECONDS * 2 ** (retryCount - 1), MAX_BACKOFF_SECONDS) * 1000;
    return waitTime;
};

export const syncAdobeSign = async (RecordIdsList: any[]): Promise<any> => {
    const promises = makeParallelAdobeSignRequests(RecordIdsList);
    const results = await Promise.allSettled(promises);

    const failedRequests = results.filter(
        (result): result is PromiseRejectedResult => result.status === "rejected" &&
            result.reason?.response?.status === 429
    );

    const fulfilledRequests = results.filter(
        (result): result is PromiseFulfilledResult<any> => result.status === "fulfilled"
    );

    return { results: fulfilledRequests, failedRequests, hasRateLimitError: failedRequests.length > 0 };
};

export const syncAdobeSignWithRetry = async (FailedRecordIdsList: any[]): Promise<any> => {
    const fulfilledRequests: PromiseFulfilledResult<any>[] = [];
    let retryCount = 0;
    const maxRetries = 3;
    let currentList = [...FailedRecordIdsList];

    while (currentList.length > 0 && retryCount < maxRetries) {
        const promises = makeParallelAdobeSignRequests(currentList);
        const results = await Promise.allSettled(promises);

        const failedRequests = results.filter(
            (result): result is PromiseRejectedResult => result.status === "rejected" &&
                result.reason?.response?.status === 429
        );

        fulfilledRequests.push(...results.filter(
            (result): result is PromiseFulfilledResult<any> => result.status === "fulfilled"
        ));

        if (failedRequests.length === 0) {
            return {
                results: fulfilledRequests,
                hasRateLimitError: false
            };
        }

        currentList = [...failedRequests.map(request => ({
            record_id: (request as PromiseRejectedResult).reason?.config?.params?.record_id
        }))];

        retryCount++;

        const backoffTime = getWaitTime(retryCount, failedRequests);
        await delay(backoffTime);
    }

    return {
        results: fulfilledRequests,
        hasRateLimitError: true
    };
};
